import SportIcon from '@/components/SportIcon';
/**
 * ScorerEncounterPage.jsx
 * Individual tie-by-tie scoring with player names and sequential match numbers.
 * Different sports can be assigned to different scorers via scorer_sports.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Play, Square, Clock, CheckCircle2,
  Trophy, RefreshCw, Edit2, Check, User, Save
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';

const SPORT_ICONS = {
  table_tennis:'🏓', badminton:'🏸', chess:'♟️',
  carrom:'🎯', pickleball:'🥒', squash:'🎾',
  cricket:'🏏', football:'⚽',
};
const SPORT_LABELS = {
  table_tennis:'Table Tennis', badminton:'Badminton', chess:'Chess',
  carrom:'Carrom', pickleball:'Pickleball', squash:'Squash',
};
const SLOT_COLOR  = { P:'#f5c518', R:'#3b82f6' };
const slotColor   = (slot='') => SLOT_COLOR[slot?.[0]] || '#8a93a3';

// Hardcoded sport defaults — used when DB format doesn't have game_points yet
const SPORT_SCORING_DEFAULTS = {
  table_tennis: { game_points: 11, score_type: 'points', label: '11 pts/game' },
  badminton:    { game_points: 21, score_type: 'points', label: '21 pts/game' },
  pickleball:   { game_points: 15, score_type: 'points', label: '15 pts/game' },
  chess:        { game_points: 0,  score_type: 'result', label: 'Win/Draw/Loss' },
  carrom:       { game_points: 0,  score_type: 'result', label: 'Win/Draw/Loss' },
};

export default function ScorerEncounterPage() {
  const { encId }   = useParams();
  const navigate    = useNavigate();
  const { activeOrgSlug, user } = useAuthStore();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';
  const token = () => localStorage.getItem('btccp_access_token');

  const [enc, setEnc]               = useState(null);
  const [sportFormats, setSportFormats] = useState({});
  const [scorerSports, setScorerSports] = useState([]);
  const [rosters, setRosters]           = useState({ home:[], away:[] });
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  // Editable time fields
  const [editingTime, setEditingTime] = useState(null);
  const [editTimeVal, setEditTimeVal] = useState('');
  // Per-match editing: { "sport_matchIdx": { home_player, away_player, home_score, away_score } }
  const [dirtyMatches, setDirtyMatches] = useState({});

  useEffect(() => { fetchEnc(); fetchScorerSports(); }, [encId, orgSlug]);// eslint-disable-line react-hooks/exhaustive-deps

  const fetchScorerSports = async () => {
    try {
      const token_ = token();
      const res = await fetch(`/api/orgs/${orgSlug}/users`, { headers: { 'Authorization': `Bearer ${token_}` } });
      if (!res.ok) return;
      const users = await res.json();
      const me = users.find(u => u.phone === user?.phone);
      setScorerSports(me?.scorer_sports || []);
    } catch (fetchErr) { console.warn('Scorer sports fetch failed:', fetchErr?.message); }
  };

  const fetchEnc = async () => {
    setLoading(true);
    try {
      const listRes = await fetch(`/api/orgs/${orgSlug}/scorer/encounters`,
        { headers: { 'Authorization': `Bearer ${token()}` } });
      const list = listRes.ok ? await listRes.json() : [];
      const found = list.find(e => e.id === encId);
      if (!found) { toast.error('Match not found'); navigate('/score'); return; }
      setEnc(found);
      if (found.tournament_id) {
        const fmtRes = await fetch(`/api/orgs/${orgSlug}/tournaments/${found.tournament_id}/sport-formats`,
          { headers: { 'Authorization': `Bearer ${token()}` } });
        if (fmtRes.ok) {
          const fmts = await fmtRes.json();
          const map = {};
          fmts.forEach(f => { map[f.sport_code] = f; });
          setSportFormats(map);
        }
      }
    } catch (_) { toast.error('Failed to load match'); }
    finally { setLoading(false); }
  };

  // Build flat list of all ties with sequential match numbers
  const buildTies = () => {
    if (!enc) return [];
    let n = 0;
    const ties = [];
    (enc.sport_results || []).forEach(sr => {
      (sr.matches || []).forEach((m, idx) => {
        n++;
        ties.push({
          n,                          // sequential match number
          sport_code: sr.sport_code,
          sport_name: SPORT_LABELS[sr.sport_code] || sr.sport_code,
          matchIdx: idx,
          slot: m.slot || m.label || `M${idx+1}`,
          // Current values (dirty override or original)
          home_player:  dirtyMatches[`${sr.sport_code}_${idx}`]?.home_player ?? (m.home_player || ''),
          away_player:  dirtyMatches[`${sr.sport_code}_${idx}`]?.away_player ?? (m.away_player || ''),
          home_score:   dirtyMatches[`${sr.sport_code}_${idx}`]?.home_score  ?? m.home_score ?? '',
          away_score:   dirtyMatches[`${sr.sport_code}_${idx}`]?.away_score  ?? m.away_score ?? '',
          winner:       m.winner,
          canScore: scorerSports.length === 0 || scorerSports.includes(sr.sport_code),
        });
      });
    });
    return ties;
  };

  const updateField = (sport, idx, field, value) => {
    const key = `${sport}_${idx}`;
    setDirtyMatches(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value }
    }));
  };

  const saveAllScores = async () => {
    setSaving(true);
    try {
      // Merge dirty values into enc.sport_results
      const updatedResults = (enc.sport_results || []).map(sr => ({
        ...sr,
        matches: (sr.matches || []).map((m, idx) => {
          const dirty = dirtyMatches[`${sr.sport_code}_${idx}`];
          if (!dirty) return m;
          const merged = { ...m };
          if (dirty.home_player  !== undefined) merged.home_player  = dirty.home_player;
          if (dirty.away_player  !== undefined) merged.away_player  = dirty.away_player;
          if (dirty.home_player2 !== undefined) merged.home_players = [dirty.home_player || m.home_player||'', dirty.home_player2];
          if (dirty.away_player2 !== undefined) merged.away_players = [dirty.away_player || m.away_player||'', dirty.away_player2];
          if (dirty.home_score   !== undefined) merged.home_score   = parseInt(dirty.home_score) || 0;
          if (dirty.away_score   !== undefined) merged.away_score   = parseInt(dirty.away_score) || 0;
          return merged;
        }),
      }));
      const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${enc.tournament_id}/encounters/${encId}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ sport_results: updatedResults }),
      });
      if (res.ok) {
        const updated = await res.json();
        setEnc(prev => ({ ...prev, ...updated, home_team: prev.home_team, away_team: prev.away_team,
          tournament_name: prev.tournament_name, start_time: prev.start_time, end_time: prev.end_time }));
        setDirtyMatches({});
        toast.success('All scores saved · Points recalculated');
      } else toast.error('Save failed');
    } catch (_) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const recordTime = async (field) => {
    const now = new Date().toISOString();
    try {
      await fetch(`/api/orgs/${orgSlug}/scorer/encounters/${encId}/time`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: now }),
      });
      setEnc(prev => ({ ...prev, [field]: now }));
      toast.success(field === 'start_time' ? 'Match started!' : 'Match ended!');
    } catch (_) { toast.error('Failed'); }
  };

  const localTimeToISO = (t) => { const [h,m]=t.split(':').map(Number); const d=new Date(); d.setHours(h,m,0,0); return d.toISOString(); };
  const saveEditedTime = async (field) => {
    if (!editTimeVal) { setEditingTime(null); return; }
    try {
      const iso = localTimeToISO(editTimeVal);
      await fetch(`/api/orgs/${orgSlug}/scorer/encounters/${encId}/time`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: iso }),
      });
      setEnc(prev => ({ ...prev, [field]: iso }));
      toast.success('Time updated');
    } catch (_) { toast.error('Failed'); }
    setEditingTime(null);
  };

  const handleStart = async () => {
    await recordTime('start_time');
    await fetch(`/api/orgs/${orgSlug}/tournaments/${enc.tournament_id}/encounters/${encId}`, {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'in_progress' }),
    });
    setEnc(prev => ({ ...prev, status: 'in_progress' }));
  };

  const handleFinish = async () => {
    if (!confirm('Mark this match as completed?')) return;
    await recordTime('end_time');
    await saveAllScores();
    navigate('/score');
  };

  const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}) : '—';

  if (loading) return (
    <div className="p-4 space-y-3">{[1,2,3,4].map(i=><Skeleton key={i} className="h-28 bg-[#1d2230] rounded-xl"/>)}</div>
  );
  if (!enc) return null;

  const isLive = enc.status === 'in_progress';
  const isDone = enc.status === 'completed';
  const ties   = buildTies();
  const hasDirty = Object.keys(dirtyMatches).length > 0;
  const homeLabel = enc.home_team?.name || enc.home_seed || 'Home';
  const awayLabel = enc.away_team?.name || enc.away_seed || 'Away';
  const homeColor = enc.home_team?.color || '#3b82f6';
  const awayColor = enc.away_team?.color || '#ef4444';

  return (
    <div className="p-3 sm:p-5 pb-28" data-testid="scorer-encounter-page">
      {/* Back */}
      <Button variant="ghost" onClick={() => navigate('/score')} className="mb-3 text-[#8a93a3] hover:text-[#f0f2f7] -ml-2">
        <ArrowLeft className="h-4 w-4 mr-2"/>Back
      </Button>

      {/* Match header */}
      <Card className={`mb-4 border ${isLive ? 'bg-[#161a26] ring-1 ring-[rgba(239,68,68,0.3)] border-[rgba(239,68,68,0.3)]' : 'bg-[#161a26] border-[#252a37]'}`}>
        <CardContent className="p-4">
          {/* Status */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`border text-xs ${isLive?'bg-[rgba(239,68,68,0.15)] text-[#ef4444] border-[rgba(239,68,68,0.35)]':isDone?'bg-[rgba(34,197,94,0.15)] text-[#22c55e] border-[rgba(34,197,94,0.35)]':'bg-[rgba(59,130,246,0.15)] text-[#3b82f6] border-[rgba(59,130,246,0.35)]'}`}>
                {isLive&&<span className="w-1.5 h-1.5 rounded-full bg-[#ef4444] animate-pulse mr-1"/>}
                {isLive?'LIVE':isDone?'Completed':'Scheduled'}
              </Badge>
              {enc.match_label && <span className="text-xs text-[#f5c518] font-mono">{enc.match_label}</span>}
              {enc.tournament_name && <span className="text-xs text-[#8a93a3]">{enc.tournament_name}</span>}
            </div>
            <Button variant="ghost" size="icon" onClick={fetchEnc} className="h-7 w-7 text-[#8a93a3]">
              <RefreshCw className="h-3.5 w-3.5"/>
            </Button>
          </div>

          {/* Teams */}
          <div className="flex items-center gap-3">
            <div className="flex-1 text-center">
              <div className="h-10 w-10 rounded-xl mx-auto mb-1 flex items-center justify-center font-bold text-white text-lg" style={{ backgroundColor: homeColor }}>
                {enc.home_team?.short_name?.[0] || homeLabel?.[0] || 'H'}
              </div>
              <p className={`font-bold text-sm ${enc.winner==='home'?'text-[#f5c518]':'text-[#f0f2f7]'}`}>{homeLabel}</p>
            </div>
            <div className="text-center flex-shrink-0">
              <div className="text-2xl font-black tabular-nums text-[#f5c518]">
                {enc.total_home_pts ?? 0} : {enc.total_away_pts ?? 0}
              </div>
              <p className="text-[10px] text-[#8a93a3]">{ties.length} ties</p>
            </div>
            <div className="flex-1 text-center">
              <div className="h-10 w-10 rounded-xl mx-auto mb-1 flex items-center justify-center font-bold text-white text-lg" style={{ backgroundColor: awayColor }}>
                {enc.away_team?.short_name?.[0] || awayLabel?.[0] || 'A'}
              </div>
              <p className={`font-bold text-sm ${enc.winner==='away'?'text-[#f5c518]':'text-[#f0f2f7]'}`}>{awayLabel}</p>
            </div>
          </div>

          {/* Time row */}
          <div className="flex items-center justify-center gap-6 mt-3 pt-3 border-t border-[#252a37] text-xs text-[#8a93a3]">
            {['start_time','end_time'].map(field => (
              <div key={field} className="flex items-center gap-1.5">
                {field==='start_time'?<Play className="h-3 w-3"/>:<CheckCircle2 className="h-3 w-3"/>}
                {field==='start_time'?'Start':'End'}:
                {editingTime===field ? (
                  <span className="flex items-center gap-1">
                    <Input type="time" value={editTimeVal} onChange={e=>setEditTimeVal(e.target.value)}
                      className="h-6 w-24 bg-[#1d2230] border-[#252a37] text-[#f5c518] text-xs px-1"/>
                    <button onClick={()=>saveEditedTime(field)} className="text-[#22c55e]"><Check className="h-3.5 w-3.5"/></button>
                    <button onClick={()=>setEditingTime(null)} className="text-[#8a93a3]">×</button>
                  </span>
                ) : (
                  <button onClick={()=>{setEditingTime(field);setEditTimeVal(enc[field]?new Date(enc[field]).toTimeString().slice(0,5):new Date().toTimeString().slice(0,5));}}
                    className="flex items-center gap-1 group">
                    <strong className={enc[field]?(field==='end_time'?'text-[#22c55e]':'text-[#f0f2f7]'):'text-[#8a93a3]'}>
                      {enc[field]?fmtTime(enc[field]):'—'}
                    </strong>
                    <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-60"/>
                  </button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Individual ties */}
      <div className="space-y-2">
        {/* Sport section headers + ties */}
        {(enc.sport_results || []).map(sr => (
          <div key={sr.sport_code}>
            {/* Sport divider */}
            <div className="flex items-center gap-2 py-2">
              <SportIcon sport={sr.sport_code} size={18}/>
              <span className="text-xs font-bold text-[#8a93a3] uppercase tracking-widest">
                {SPORT_LABELS[sr.sport_code] || sr.sport_code}
              </span>
              <div className="flex-1 h-px bg-[#252a37]"/>
              <span className="text-xs text-[#8a93a3]">
                {sr.home_match_wins ?? 0}W – {sr.away_match_wins ?? 0}W
              </span>
            </div>

            {/* Individual tie cards */}
            {(sr.matches || []).map((m, idx) => {
              const globalN = ties.find(t=>t.sport_code===sr.sport_code&&t.matchIdx===idx)?.n || idx+1;
              const key = `${sr.sport_code}_${idx}`;
              const dirty = dirtyMatches[key] || {};
              const isDoubles = m.type === 'doubles';
              // Doubles: use home_players[]/away_players[] arrays; singles: home_player/away_player
              const homePl  = dirty.home_player  ?? (m.home_player  || '');
              const awayPl  = dirty.away_player  ?? (m.away_player  || '');
              const homePl2 = dirty.home_player2 ?? (m.home_players?.[1] || '');
              const awayPl2 = dirty.away_player2 ?? (m.away_players?.[1] || '');
              const homeS  = dirty.home_score  ?? (m.home_score ?? '');
              const awayS  = dirty.away_score  ?? (m.away_score ?? '');
              const canScore = scorerSports.length === 0 || scorerSports.includes(sr.sport_code);
              const sc = slotColor(m.slot || m.label);
              // Sport scoring type
              const sportDef = SPORT_SCORING_DEFAULTS[sr.sport_code] || { game_points: 11, score_type: 'points' };
              const isResult = sportDef.score_type === 'result'; // chess/carrom: Win/Draw/Loss

              return (
                <Card key={idx} className={`border transition-colors ${
                  m.winner ? 'bg-[#0f1219] border-[#1e2435]' : 
                  canScore ? 'bg-[#161a26] border-[#252a37] hover:border-[#f5c518]/30' :
                  'bg-[#161a26] border-[#252a37] opacity-50'
                }`}>
                  <CardContent className="p-3">
                    {/* Tie header */}
                    <div className="flex items-center gap-2 mb-2.5">
                      <span className="text-lg font-black text-[#f5c518] w-10 text-center tabular-nums leading-none">
                        #{globalN}
                      </span>
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                        style={{ backgroundColor:`${sc}20`, color:sc }}>
                        {m.slot || m.label}
                      </span>
                      {/* Scoring rule badge */}
                      <span className="text-[10px] text-[#8a93a3] px-1.5 py-0.5 rounded bg-[#1d2230]">
                        {sportDef.label}
                      </span>
                      <div className="flex-1"/>
                      {m.winner && (
                        <span className={`text-xs font-semibold ${m.winner==='home'?'text-[#22c55e]':m.winner==='bye_home'||m.winner==='bye_away'?'text-[#f5c518]':'text-[#ef4444]'}`}>
                          {m.winner==='home'?`${homeLabel} wins`:m.winner==='away'?`${awayLabel} wins`:`BYE → ${m.winner==='bye_home'?homeLabel:awayLabel}`}
                        </span>
                      )}
                      {!canScore && <span className="text-[10px] text-[#8a93a3]">not your sport</span>}
                      {/* BYE buttons */}
                      {canScore && !isDone && !m.winner && (
                        <div className="flex gap-1 ml-auto">
                          <button
                            onClick={() => {
                              setDirtyMatches(prev => ({...prev, [`${sr.sport_code}_${idx}`]: {
                                ...(prev[`${sr.sport_code}_${idx}`]||{}),
                                home_score: 2, away_score: 0, winner: 'bye_home',
                                home_player: homePl || `${homeLabel} (BYE)`,
                                away_player: awayPl || `${awayLabel} (absent)`,
                              }}));
                            }}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-[rgba(245,197,24,0.1)] text-[#f5c518] border border-[rgba(245,197,24,0.3)] hover:bg-[rgba(245,197,24,0.2)] transition-colors"
                            title={`${homeLabel} gets BYE (away absent)`}>
                            {homeLabel[0]}↑ BYE
                          </button>
                          <button
                            onClick={() => {
                              setDirtyMatches(prev => ({...prev, [`${sr.sport_code}_${idx}`]: {
                                ...(prev[`${sr.sport_code}_${idx}`]||{}),
                                home_score: 0, away_score: 2, winner: 'bye_away',
                                home_player: homePl || `${homeLabel} (absent)`,
                                away_player: awayPl || `${awayLabel} (BYE)`,
                              }}));
                            }}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-[rgba(245,197,24,0.1)] text-[#f5c518] border border-[rgba(245,197,24,0.3)] hover:bg-[rgba(245,197,24,0.2)] transition-colors"
                            title={`${awayLabel} gets BYE (home absent)`}>
                            {awayLabel[0]}↑ BYE
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Players — 1 for singles, 2 for doubles */}
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3 text-[#8a93a3] flex-shrink-0"/>
                          <Input value={homePl} onChange={e=>updateField(sr.sport_code,idx,'home_player',e.target.value)}
                            disabled={isDone||!canScore} placeholder={`${homeLabel} P1`}
                            className="h-7 text-xs bg-[#1d2230] border-[#252a37] text-[#f0f2f7] placeholder:text-[#8a93a3]/50"/>
                        </div>
                        {isDoubles && (
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3 text-[#8a93a3] flex-shrink-0"/>
                            <Input value={homePl2} onChange={e=>updateField(sr.sport_code,idx,'home_player2',e.target.value)}
                              disabled={isDone||!canScore} placeholder={`${homeLabel} P2`}
                              className="h-7 text-xs bg-[#1d2230] border-[#252a37] text-[#f0f2f7] placeholder:text-[#8a93a3]/50"/>
                          </div>
                        )}
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3 text-[#8a93a3] flex-shrink-0"/>
                          <Input value={awayPl} onChange={e=>updateField(sr.sport_code,idx,'away_player',e.target.value)}
                            disabled={isDone||!canScore} placeholder={`${awayLabel} P1`}
                            className="h-7 text-xs bg-[#1d2230] border-[#252a37] text-[#f0f2f7] placeholder:text-[#8a93a3]/50"/>
                        </div>
                        {isDoubles && (
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3 text-[#8a93a3] flex-shrink-0"/>
                            <Input value={awayPl2} onChange={e=>updateField(sr.sport_code,idx,'away_player2',e.target.value)}
                              disabled={isDone||!canScore} placeholder={`${awayLabel} P2`}
                              className="h-7 text-xs bg-[#1d2230] border-[#252a37] text-[#f0f2f7] placeholder:text-[#8a93a3]/50"/>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Score entry */}
                    {isResult ? (
                      /* Chess / Carrom: Win / Draw / Loss buttons */
                      <div className="flex gap-1.5">
                        {[
                          { w: 'home', label: `${homeLabel} Wins`, color: '#22c55e' },
                          { w: 'draw', label: 'Draw',              color: '#f5c518' },
                          { w: 'away', label: `${awayLabel} Wins`, color: '#ef4444' },
                        ].map(opt => (
                          <button key={opt.w}
                            disabled={isDone||!canScore}
                            onClick={() => {
                              updateField(sr.sport_code, idx, 'home_score', opt.w === 'home' ? 1 : 0);
                              updateField(sr.sport_code, idx, 'away_score', opt.w === 'away' ? 1 : 0);
                              // Also store winner directly so backend can read it
                              setDirtyMatches(prev => ({
                                ...prev,
                                [`${sr.sport_code}_${idx}`]: {
                                  ...(prev[`${sr.sport_code}_${idx}`] || {}),
                                  home_score: opt.w === 'home' ? 1 : 0,
                                  away_score: opt.w === 'away' ? 1 : 0,
                                }
                              }));
                            }}
                            className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-40 ${
                              m.winner === opt.w
                                ? 'border-current text-current'
                                : 'bg-[#1d2230] border-[#252a37] text-[#8a93a3] hover:border-current hover:text-current'
                            }`}
                            style={{ color: m.winner === opt.w ? opt.color : undefined,
                                     borderColor: m.winner === opt.w ? opt.color : undefined }}>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    ) : (
                      /* TT / Badminton / Pickleball: numeric score inputs */
                      <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
                        <Input type="number" min="0" max={sportDef.game_points + 10} value={homeS}
                          onChange={e=>updateField(sr.sport_code,idx,'home_score',e.target.value)}
                          disabled={isDone||!canScore}
                          placeholder={String(sportDef.game_points)}
                          className={`h-10 text-center text-xl font-bold bg-[#1d2230] border-[#252a37] focus:border-[#f5c518] focus:outline-none ${m.winner==='home'?'text-[#22c55e]':'text-[#f0f2f7]'}`}/>
                        <span className="text-[#8a93a3] font-bold text-base text-center">:</span>
                        <Input type="number" min="0" max={sportDef.game_points + 10} value={awayS}
                          onChange={e=>updateField(sr.sport_code,idx,'away_score',e.target.value)}
                          disabled={isDone||!canScore}
                          placeholder={String(sportDef.game_points)}
                          className={`h-10 text-center text-xl font-bold bg-[#1d2230] border-[#252a37] focus:border-[#f5c518] focus:outline-none ${m.winner==='away'?'text-[#22c55e]':'text-[#f0f2f7]'}`}/>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ))}
      </div>

      {/* Action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-[#10131c]/95 backdrop-blur border-t border-[#252a37] p-4">
        <div className="flex items-center gap-2 max-w-2xl mx-auto">
          {!isLive && !isDone && (
            <Button onClick={handleStart} className="flex-1 h-12 bg-[#22c55e] hover:bg-[#16a34a] text-white font-semibold">
              <Play className="h-4 w-4 mr-2"/>Start Match
            </Button>
          )}
          {(isLive || isDone) && (
            <>
              <Button onClick={saveAllScores} disabled={saving||!hasDirty} variant="outline"
                className={`flex-1 h-12 font-semibold ${hasDirty?'border-[#f5c518] text-[#f5c518] hover:bg-[rgba(245,197,24,0.08)]':'border-[#252a37] text-[#8a93a3]'}`}>
                <Save className="h-4 w-4 mr-2"/>
                {saving?'Saving...':hasDirty?`Save ${Object.keys(dirtyMatches).length} change${Object.keys(dirtyMatches).length!==1?'s':''}`:'All saved'}
              </Button>
              {!isDone && (
                <Button onClick={handleFinish} disabled={saving}
                  className="h-12 px-6 bg-[#ef4444] hover:bg-[#dc2626] text-white font-semibold flex-shrink-0">
                  <Square className="h-4 w-4 mr-2"/>Finish
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
